import { useEffect, useMemo, useRef, useState } from 'react';

import type { LoginWaves as WavesOptions } from '@/lib/login/loginDesign';

/**
 * 커서 물결(Emitter Cursor · Waves) — <b>이식판</b>(사용자 지시 2026-09-15).
 *
 * <h2>어디서 왔나</h2>
 * 커서 꼬리와 <b>같은 묶음</b>의 첫째 시안이다 — Codrops 의 `cursors-emitter`(Marlène Bruhat)
 * 넷(Waves · Trail · Tube · Mask) 중 하나(`index.html` · `cursor1.js`).
 * 데모 `https://tympanus.net/Development/EmitterCursor/index.html` ·
 * 기사 `https://tympanus.net/codrops/2022/08/24/custom-svg-cursors-with-an-interactive-emitter-effect/`
 *
 * <h2>어떻게 생긴 움직임인가</h2>
 * 커서 자리에 <b>동심원</b>을 화면 대각선만큼 깔아 두고, 프레임마다 모든 원의 중심을 커서 자리로
 * 옮긴다. 옮기는 것은 같은데 <b>따라오는 속도가 다르다</b> — 원마다 CSS 전환 시간을
 * `18 + i×spread` ms 로 주면 바깥 원일수록 늦게 도착해, 커서를 움직이는 순간 원들이 뒤로 늘어졌다
 * 오므라들며 물결이 된다.
 *
 * <ul>
 *   <li>반지름 <code>점 반지름 × 3 + i × 간격</code> — 원본 <code>radiusStart = 3 × radiusCursor</code></li>
 *   <li>고리 수 <code>(대각선 + 간격 − 첫 반지름) ÷ 간격</code> — 가장 바깥 고리가 화면 귀퉁이에 닿는다</li>
 *   <li>선만 그린다(<code>fill:none</code>). 굵기 1.5 · 불투명 0.15 가 원본 값이다</li>
 * </ul>
 *
 * <h2>꼬리와 갈라지는 점</h2>
 * 꼬리는 <b>굵은 덩어리</b>가 겹쳐 띠를 만들고, 물결은 <b>얇은 선</b>이라 사진을 가리지 않는다 —
 * 사용자가 수평 시차와 짝지은 이유가 그것이다. 대신 무늬가 옅어 사진 위에서는 원본의 0.15 보다
 * 조금 올려 쓴다({@code opacity}).
 *
 * <h2>진짜 커서를 숨기지 않는다 ★</h2>
 * 원본은 `cursor:none` 으로 <b>시스템 커서를 지우고</b> 제 점을 대신 세운다. 여기서는 그렇게 하지
 * 않는다 — 이 층 아래에 아이디·비밀번호 칸이 있고, 거기 무언가를 <b>입력하러 온 사람</b>에게서
 * 화살표를 뺏을 이유가 없다. 점은 그대로 그리되 화살표 밑에 깔린 «빛» 으로 읽힌다.
 *
 * <h2>무지개</h2>
 * 위 색·아래 색을 고르는 대신 <b>색상환을 돌게</b> 할 수 있다({@code rainbow} · 사용자 지시
 * 2026-09-16). 두 끝이 어긋난 속도로 돌아 조합이 계속 바뀐다 — 자세한 것은 {@code RAINBOW_*}.
 *
 * <h2>고리는 DOM 에 직접 적는다</h2>
 * 수백 개를 프레임마다 옮기므로 React 로 값을 내리지 않는다 — 상태로 돌리면 한 프레임에 수백 번
 * 렌더가 된다. 여기서 React 가 하는 일은 «고리를 세우는 것» 까지다.
 */

/** 원본 `cursor1.js` 의 바탕색. 아래에 사진이 없을 때만 깐다(꼬리와 같은 값). */
const BACK = '#181825';

/** 원본 `transitionParticles.duration`. 여기에 고리 번호 × {@code spreadMs} 가 더해진다. */
const BASE_MS = 18;

/** 원본 `setTinyCursor` — 빠르게 움직일수록 점이 진행 방향으로 늘어난다. */
const ACCELERATOR = 1000;
const MAX_SQUEEZE = 0.6;

/** 고리를 몇 개까지 세울 것인가. 원본은 상한이 없지만 그쪽은 이 층 하나가 화면의 전부다. */
const MAX_RINGS = 400;

/**
 * 무지개(사용자 지시 2026-09-16) — 위 색과 아래 색이 <b>서로 다른 속도</b>로 색상환을 돈다.
 *
 * <p>같은 속도로 돌리면 둘 사이 간격이 고정돼 「같은 배색이 통째로 회전하는」 것으로 보인다.
 * 속도를 어긋나게 두면 두 색이 천천히 벌어졌다 겹쳤다 하며 조합이 매번 달라진다 — 프레임마다
 * 난수를 뽑지 않는 이유는 그러면 색이 도는 것이 아니라 <b>깜빡이기</b> 때문이다. 시작 색만
 * 화면을 열 때 한 번 뽑는다.
 */
const RAINBOW_DEG_PER_SEC = 24;
const RAINBOW_LAG = 0.63;

/** 색상환 위의 한 점. 채도·밝기는 원본 두 색(보라·청록)의 결에 맞춘다. */
function hue(deg: number): string {
  return `hsl(${((deg % 360) + 360) % 360} 92% 62%)`;
}

export { LOGIN_WAVES_STYLES } from './loginWavesStyles';

export function LoginWaves({ options, solid }: { options: WavesOptions; solid: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const ringsRef = useRef<(SVGCircleElement | null)[]>([]);
  const dotRef = useRef<SVGCircleElement>(null);
  const stop1Ref = useRef<SVGStopElement>(null);
  const stop2Ref = useRef<SVGStopElement>(null);

  const { dotRadius, ringGap, lineWidth, spreadMs, opacity, color1, color2, rainbow } = options;

  /* 그리는 고리가 읽는 값. 만질 때마다 고리를 다시 세우면 물결이 그 순간 한 점으로 모인다. */
  const params = useRef({ speed: options.speed, rainbow, color1, color2 });
  params.current = { speed: options.speed, rainbow, color1, color2 };

  /** 무지개가 도는 출발점. 화면을 열 때 한 번 뽑는다 — 열 때마다 다른 배색으로 시작한다. */
  const seed = useRef({ a: Math.random() * 360, b: Math.random() * 360 });

  /** 칸 대각선. 여기서 고리 수가 나온다 — 가장 바깥 고리가 귀퉁이에 닿아야 물결이 안 끊긴다. */
  const [diagonal, setDiagonal] = useState(1600);

  /** 첫 고리 반지름. 원본 `radiusStart = 3 × radiusCursor`. */
  const firstRadius = dotRadius * 3;

  const ringCount = useMemo(() => {
    const fits = Math.round((diagonal + ringGap - firstRadius) / ringGap);
    return Math.max(1, Math.min(MAX_RINGS, fits));
  }, [diagonal, ringGap, firstRadius]);

  /** 그라디언트 id 는 문서 안에서 유일해야 한다 — 미리보기와 진짜 화면이 한 문서에 설 수 있다. */
  const gradientId = useMemo(() => `lwx-grad-${Math.random().toString(36).slice(2, 9)}`, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    /* <b>레이아웃 크기</b>로 잰다({@code offsetWidth}) — 설정 화면의 미리보기는 판 전체를
       {@code transform: scale} 로 줄여 놓아 {@code getBoundingClientRect} 가 «줄어든» 크기를 준다. */
    const size = { w: 1, h: 1 };
    const measure = () => {
      const w = host.offsetWidth;
      const h = host.offsetHeight;
      if (!w || !h) return;
      size.w = w;
      size.h = h;
      setDiagonal(Math.ceil(Math.hypot(w, h)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);

    const mouse = { x: size.w / 2, y: size.h / 2 };
    const pos = { x: mouse.x, y: mouse.y };

    /* 창에서 듣는다. 이 칸은 `pointer-events:none` 이라 제 위에서 일어난 일을 듣지 못한다.
       거기서는 판이 줄어 있으므로, 화면에서 잰 거리를 <b>줄이기 전 좌표로 되돌린다</b>. */
    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const k = host.offsetWidth / r.width;
      mouse.x = (e.clientX - r.left) * k;
      mouse.y = (e.clientY - r.top) * k;
    };
    window.addEventListener('pointermove', onMove, { passive: true });

    let visible = false;
    const io = new IntersectionObserver((es) => {
      visible = es.some((e) => e.isIntersecting);
    }, { rootMargin: '100px' });
    io.observe(host);

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible || document.hidden) return;

      const dx = mouse.x - pos.x;
      const dy = mouse.y - pos.y;
      pos.x += dx * params.current.speed;
      pos.y += dy * params.current.speed;

      /* 원본 setParticles(전환판) — 모든 고리에 <b>같은 자리</b>를 적는다. 늦게 따라오는 일은
         고리마다 다른 전환 시간이 한다(그 값은 React 가 세울 때 이미 박아 뒀다). */
      const x = String(pos.x);
      const y = String(pos.y);
      for (const ring of ringsRef.current) {
        if (!ring) continue;
        ring.setAttribute('cx', x);
        ring.setAttribute('cy', y);
      }

      // 원본 setTinyCursor — 빠를수록 진행 방향으로 늘어나고 그 직각으로 눌린다.
      const dot = dotRef.current;
      if (dot) {
        dot.setAttribute('cx', x);
        dot.setAttribute('cy', y);
        const squeeze = Math.min(Math.hypot(dx, dy) / ACCELERATOR, MAX_SQUEEZE);
        dot.style.transformOrigin = `${pos.x}px ${pos.y}px`;
        dot.style.transform =
          `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI}deg) scale(${1 + squeeze}, ${1 - squeeze})`;
      }

      /* 그라디언트 두 끝. 무지개가 꺼져 있어도 <b>여기서</b> 적는다 — 켰다 끄는 순간 React 는 제가
         내려 둔 값이 그대로인 줄 알아 다시 칠하지 않고, 돌던 색이 그 자리에 굳는다. */
      const t = performance.now() / 1000;
      const top = params.current.rainbow ? hue(seed.current.a + t * RAINBOW_DEG_PER_SEC) : params.current.color1;
      const bottom = params.current.rainbow
        ? hue(seed.current.b + t * RAINBOW_DEG_PER_SEC * RAINBOW_LAG)
        : params.current.color2;
      stop1Ref.current?.setAttribute('stop-color', top);
      stop2Ref.current?.setAttribute('stop-color', bottom);
      dot?.setAttribute('fill', bottom);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
    };
  }, [ringCount]);

  return (
    <div
      className="lwx"
      ref={hostRef}
      style={{
        background: solid ? BACK : 'transparent',
        // 사진 위에서는 «빛» 으로 얹힌다. 혼자 설 때는 원본처럼 그대로 칠한다.
        mixBlendMode: solid ? 'normal' : 'screen',
      }}
    >
      <svg>
        <defs>
          {/* 원본은 세로 그라디언트다(x1,y1 0% → x2,y2 0%,100%) — 고리가 위아래로 색이 갈린다. */}
          {/* 두 끝의 색은 프레임마다 위에서 적는다(무지개). 여기 값은 첫 프레임 전의 모습이다. */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop ref={stop1Ref} offset="0%" stopColor={color1} />
            <stop ref={stop2Ref} offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
        {Array.from({ length: ringCount }, (_, i) => (
          <circle
            key={i}
            ref={(el) => {
              ringsRef.current[i] = el;
            }}
            r={firstRadius + i * ringGap}
            cx={0}
            cy={0}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={lineWidth}
            strokeOpacity={opacity}
            // 바깥 고리일수록 늦게 도착한다. 이 한 줄이 «물결» 의 전부다(원본 transitionParticles).
            style={{ transitionDuration: `${BASE_MS + i * spreadMs}ms` }}
          />
        ))}
        {/* 커서 자리의 점. 진짜 화살표를 지우지 않으므로 그 밑에 깔린 빛으로 읽힌다. */}
        {dotRadius > 0 && (
          <circle ref={dotRef} r={dotRadius} cx={0} cy={0} fill={color2} fillOpacity={0.9} />
        )}
      </svg>
    </div>
  );
}
